import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type {
  CreateStressScenarioRequest,
  StressRun,
  StressScenario,
} from './types';

// ── Query keys ────────────────────────────────────────────────────────────────

const stressKeys = {
  scenarios:          () => ['alm', 'stress', 'scenarios'] as const,
  scenarioRuns:       (id: string) => ['alm', 'stress', 'scenario', id, 'runs'] as const,
  runs:               () => ['alm', 'stress', 'runs'] as const,
  run:                (id: string) => ['alm', 'stress', 'runs', id] as const,
};

// ── Scenarios ─────────────────────────────────────────────────────────────────

/**
 * GET /api/v1/alm/stress/scenarios
 *
 * Returns all stress scenarios for the tenant, newest first.
 */
export function useStressScenarios(
  options?: Omit<
    UseQueryOptions<StressScenario[], CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: stressKeys.scenarios(),
    queryFn: () =>
      apiGet<StressScenario[]>(apiPath('alm', 'stress', 'scenarios')),
    ...options,
  });
}

/**
 * POST /api/v1/alm/stress/scenarios
 *
 * Creates a new stress scenario in DRAFT status.
 */
export function useCreateStressScenario(
  options?: UseMutationOptions<
    StressScenario,
    CubixApiError,
    CreateStressScenarioRequest
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPost<StressScenario, CreateStressScenarioRequest>(
        apiPath('alm', 'stress', 'scenarios'),
        body,
      ),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: stressKeys.scenarios() });
      return options?.onSuccess?.(...args);
    },
  });
}

// ── Run a scenario ────────────────────────────────────────────────────────────

interface RunScenarioVars {
  scenarioId: string;
  calculationDate?: string;   // ISO yyyy-MM-dd; omit = use latest baseline
}

/**
 * POST /api/v1/alm/stress/scenarios/{id}/run
 *
 * Executes the stress engine against the latest baseline for the scenario.
 * Returns 201 Created with the full StressRun result.
 */
export function useRunStressScenario(
  options?: UseMutationOptions<StressRun, CubixApiError, RunScenarioVars>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, calculationDate }) => {
      const url = calculationDate
        ? `${apiPath('alm', 'stress', 'scenarios', scenarioId, 'run')}?calculationDate=${encodeURIComponent(calculationDate)}`
        : apiPath('alm', 'stress', 'scenarios', scenarioId, 'run');
      return apiPost<StressRun, Record<string, never>>(url, {});
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: stressKeys.runs() });
      qc.invalidateQueries({ queryKey: stressKeys.scenarioRuns(args[0].scenarioId) });
      return options?.onSuccess?.(...args);
    },
  });
}

// ── Runs ──────────────────────────────────────────────────────────────────────

/**
 * GET /api/v1/alm/stress/runs
 *
 * Returns the 20 most recent stress runs across all scenarios.
 */
export function useLatestStressRuns(
  options?: Omit<
    UseQueryOptions<StressRun[], CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: stressKeys.runs(),
    queryFn: () => apiGet<StressRun[]>(apiPath('alm', 'stress', 'runs')),
    ...options,
  });
}

/**
 * GET /api/v1/alm/stress/runs/{id}
 *
 * Returns a single stress run by UUID.
 */
export function useStressRun(
  id: string | undefined,
  options?: Omit<
    UseQueryOptions<StressRun, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: stressKeys.run(id!),
    enabled: Boolean(id),
    queryFn: () => apiGet<StressRun>(apiPath('alm', 'stress', 'runs', id!)),
    ...options,
  });
}

/**
 * GET /api/v1/alm/stress/scenarios/{id}/runs
 *
 * Returns all runs for a specific scenario, newest first.
 */
export function useScenarioRuns(
  scenarioId: string | undefined,
  options?: Omit<
    UseQueryOptions<StressRun[], CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: stressKeys.scenarioRuns(scenarioId!),
    enabled: Boolean(scenarioId),
    queryFn: () =>
      apiGet<StressRun[]>(
        apiPath('alm', 'stress', 'scenarios', scenarioId!, 'runs'),
      ),
    ...options,
  });
}
